import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import {
	AgrementsReturnType,
	AgrementsType,
	IAgrements,
	ICreateAgrements
} from './types';
import {
	deleteAgrement as deleteAgrementAction,
	fetchAgrements,
	fetchAgrementsQuery,
	fetchSingleAgrement,
	publishAgrement as publishAgrementAction,
	postAgrement
} from './actions';

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useAgrements<T extends IAgrements | IAgrements[]>(
	typeParam: AgrementsType | undefined = undefined,
	parsId: number | undefined = undefined,
	queryParam: string | undefined = undefined,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined
): AgrementsReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingCTA, setLoadingCTA] = useState<boolean>(false);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? 10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getAgrements = useCallback(
		async (type: AgrementsType, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchAgrements(
					type,
					pageParam,
					itemsParam,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getAgrementsQuery = useCallback(
		async (query: string, queryPage: number, queryItems: number) => {
			try {
				setLoading(true);
				const response = await fetchAgrementsQuery(
					query,
					queryPage,
					queryItems,
					handleResponse,
					typeParam
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData, typeParam]
	);

	const getAgrement = useCallback(
		async (id: number) => {
			try {
				setLoading(true);
				const response = await fetchSingleAgrement(id);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const createAgrement = useCallback(
		async (obj: ICreateAgrements, onSuccess?: (createdId?: number) => void) => {
			try {
				setLoadingCTA(true);
				const response = await postAgrement(obj);
				if (response.status === 201) {
					onSuccess && onSuccess(response.id);
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	const publishAgrement = useCallback(
		async (id: number, onSuccess?: () => void) => {
			try {
				setLoadingCTA(true);
				const response = await publishAgrementAction(id);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},

		[]
	);

	const removeAgrement = useCallback(
		async (id: number, onSuccess?: () => void) => {
			try {
				setLoadingCTA(true);
				const response = await deleteAgrementAction(id);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},
		[]
	);

	useEffect(() => {
		if (typeParam && !queryParam) {
			getAgrements(typeParam, page, items);
		}
		 
	}, [typeParam, queryParam, page, items]);

	useEffect(() => {
		if (parsId) {
			getAgrement(parsId);
		}
		 
	}, [parsId]);

	useEffect(() => {
		if (queryParam && parsId === undefined) {
			getAgrementsQuery(queryParam, page, items);
		}
		 
	}, [queryParam, parsId, page, items]);

		return {
			data: data || (EMPTY_LIST as IAgrements[] as T),
			setData: setData as stateSetter<T>,
			loading,
			loadingCTA,
			getAgrements,
			createAgrement,
			getAgrement,
			publishAgrement,
			removeAgrement,
			items,
			page,
			setItems,
			setPage,
		totalItems,
		totalPages
	};
}
